import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { StatementPicker } from "./StatementPicker";
import { uploadFiles } from "@/lib/uploads";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DB_FUNDS = ["PSS", "CSS", "MSBS", "DFRDB", "Other"];

const NEXT_STEPS = [
  { n: "01", text: "We reply within one business day to arrange a time that suits you." },
  { n: "02", text: "A complimentary first conversation — no advice, no obligation, no pitch." },
  { n: "03", text: "A written, fixed-fee proposal before you commit to anything." },
];

const AU_PHONE_RE = /^(?:\+?61|0)[2-478]\d{8}$/;
const normalizePhone = (v) => v.replace(/[\s()\-.]/g, "");

const fieldClass =
  "w-full bg-transparent border-b border-white/25 focus:border-[#F9F8F4] outline-none py-3 text-base md:text-sm text-[#F9F8F4] placeholder:text-white/80 transition-[border-color] duration-300";

const labelClass = "block text-[10px] uppercase tracking-[0.25em] text-white mb-1";

export const EnquiryForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    household_income: "",
    household_super: "",
    db_fund_value: "",
    db_fund: "",
    debt_level: "",
    other_assets: "",
    cost_of_living: "",
    age: "",
    message: "",
    website: "",
  });
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!AU_PHONE_RE.test(normalizePhone(form.phone))) {
      toast.error("Please enter a valid Australian phone number.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, { ...form, file_count: files.length });
      const { failed } = data.upload_token
        ? await uploadFiles("enquiries", data.id, data.upload_token, files, setProgress)
        : { failed: 0 };
      toast.success("Enquiry sent — we'll be in touch within one business day.");
      if (failed) toast.error(`${failed} file${failed > 1 ? "s" : ""} couldn't be uploaded — you can email them to hello@nuaprivatewealth.com.au.`);
      setFiles([]);
      setForm({ name: "", email: "", phone: "", household_income: "", household_super: "", db_fund_value: "", db_fund: "", debt_level: "", other_assets: "", cost_of_living: "", age: "", message: "", website: "" });
    } catch (err) {
      toast.error("Your enquiry couldn't be sent. Please try again in a moment.");
    } finally {
      setSending(false);
      setProgress(null);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#0A2333] py-16 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-14 md:gap-16">
        <div data-testid="contact-divider" aria-hidden="true" className="md:col-span-12 flex items-center justify-center gap-6 -mt-4 -mb-2">
          <span className="h-px w-16 md:w-28 bg-white/15" />
          <img src="/assets/nua-mark-transparent.png" alt="" className="h-9 w-auto opacity-90" />
          <span className="h-px w-16 md:w-28 bg-white/15" />
        </div>
        <div className="md:col-span-5">
          <Reveal>
            <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white">
              <span className="h-px w-10 bg-[#F9F8F4]/50" aria-hidden="true" />
              Start the conversation
            </p>
            <h2 data-testid="contact-heading" className="mt-6 font-serif text-3xl md:text-4xl tracking-tight text-[#F9F8F4]">
              Let's talk about your financial future.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-white max-w-md">
              Your first conversation with NUA is complimentary and without obligation. Tell us
              what you'd like to achieve, ask questions and discover whether we're the right fit
              for your financial needs.
            </p>
          </Reveal>

          <div className="mt-12 space-y-8" data-testid="contact-next-steps">
            {NEXT_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={0.1 + i * 0.08}>
                <div className="flex gap-6 items-baseline border-t border-white/10 pt-6">
                  <span className="font-serif italic text-2xl text-[#A78558]">{s.n}</span>
                  <p className="text-sm leading-relaxed text-white">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.35}>
            <div className="mt-12 text-sm text-white space-y-1" data-testid="contact-details">
              <p>hello@nuaprivatewealth.com.au</p>
              <p>0411 842 648</p>
              <p>Office 845, Level 8, 121 Marcus Clarke Street, Canberra ACT 2601</p>
            </div>
          </Reveal>

          <Reveal delay={0.4}>
            <div className="mt-8 overflow-hidden border border-white/10" data-testid="contact-map-frame">
              <iframe
                data-testid="contact-map"
                title="NUA Private Wealth office — Office 845, Level 8, 121 Marcus Clarke Street, Canberra ACT 2601"
                src="https://maps.google.com/maps?q=121%20Marcus%20Clarke%20Street%2C%20Canberra%20ACT%202601&t=&z=16&ie=UTF8&iwloc=&output=embed"
                className="h-64 w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-7">
          <Reveal delay={0.15}>
            <form data-testid="enquiry-form" onSubmit={submit} className="space-y-9">
              <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden" style={{ position: "absolute" }}>
                <label htmlFor="enquiry-website">Website</label>
                <input
                  id="enquiry-website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={update("website")}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-9">
                <div>
                  <label htmlFor="enquiry-name" className={labelClass}>Full name</label>
                  <input
                    id="enquiry-name"
                    data-testid="enquiry-name-input"
                    type="text"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Citizen"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-email" className={labelClass}>Email</label>
                  <input
                    id="enquiry-email"
                    data-testid="enquiry-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@example.com.au"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-phone" className={labelClass}>Phone</label>
                  <input
                    id="enquiry-phone"
                    data-testid="enquiry-phone-input"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="04 0000 0000"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-age" className={labelClass}>Age</label>
                  <input
                    id="enquiry-age"
                    data-testid="enquiry-age-input"
                    type="text"
                    value={form.age}
                    onChange={update("age")}
                    placeholder="e.g. 58"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-income" className={labelClass}>Household income</label>
                  <input
                    id="enquiry-income"
                    data-testid="enquiry-income-input"
                    type="text"
                    value={form.household_income}
                    onChange={update("household_income")}
                    placeholder="e.g. $180,000 p.a."
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-debt" className={labelClass}>Debt level</label>
                  <input
                    id="enquiry-debt"
                    data-testid="enquiry-debt-input"
                    type="text"
                    value={form.debt_level}
                    onChange={update("debt_level")}
                    placeholder="e.g. $420,000 mortgage"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-super" className={labelClass}>Household super balance (non defined benefit)</label>
                  <input
                    id="enquiry-super"
                    data-testid="enquiry-super-input"
                    type="text"
                    value={form.household_super}
                    onChange={update("household_super")}
                    placeholder="e.g. $650,000"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-db-value" className={labelClass}>Defined benefit fund value or pension per annum</label>
                  <input
                    id="enquiry-db-value"
                    data-testid="enquiry-db-value-input"
                    type="text"
                    value={form.db_fund_value}
                    onChange={update("db_fund_value")}
                    placeholder="e.g. 500,000 or $50,000 pa"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-other-assets" className={labelClass}>Other assets value</label>
                  <input
                    id="enquiry-other-assets"
                    data-testid="enquiry-other-assets-input"
                    type="text"
                    value={form.other_assets}
                    onChange={update("other_assets")}
                    placeholder="e.g. cash, shares, property etc."
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-cost-of-living" className={labelClass}>Estimated cost of living</label>
                  <input
                    id="enquiry-cost-of-living"
                    data-testid="enquiry-cost-of-living-input"
                    type="text"
                    value={form.cost_of_living}
                    onChange={update("cost_of_living")}
                    placeholder="e.g. 75,000 or N/A"
                    className={fieldClass}
                  />
                </div>
                <div>
                  <label htmlFor="enquiry-db-fund" className={labelClass}>Defined benefit fund</label>
                  <select
                    id="enquiry-db-fund"
                    data-testid="enquiry-db-fund-select"
                    value={form.db_fund}
                    onChange={update("db_fund")}
                    className={`${fieldClass} appearance-none cursor-pointer [&>option]:text-[#111413]`}
                  >
                    <option value="">Select if applicable</option>
                    {DB_FUNDS.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="enquiry-message" className={labelClass}>How can we help?</label>
                <textarea
                  id="enquiry-message"
                  data-testid="enquiry-message-input"
                  required
                  minLength={10}
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="A few sentences about your situation is plenty — for example, approaching retirement and unsure whether your super is on track."
                  className={`${fieldClass} resize-none`}
                />
              </div>
              <StatementPicker
                files={files}
                onChange={setFiles}
                onError={toast.error}
                progress={progress}
                label="Supporting documents (optional)"
                hint="Attach statements, payslips or anything that helps explain your situation"
                testid="enquiry"
              />
              <div className="flex flex-wrap items-center gap-6">
                <button
                  data-testid="enquiry-submit-button"
                  type="submit"
                  disabled={sending}
                  className="inline-flex items-center gap-2 rounded-full bg-[#F9F8F4] px-8 py-4 text-xs uppercase tracking-[0.15em] text-[#0A2333] transition-[background-color,transform,opacity] duration-300 hover:bg-white hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {sending ? (
                    <>
                      {progress ? "Uploading" : "Sending"}
                      <Loader2 size={15} className="animate-spin" />
                    </>
                  ) : (
                    <>
                      Send enquiry
                      <ArrowRight size={15} />
                    </>
                  )}
                </button>
                <p className="text-xs text-white max-w-xs">
                  No obligation. Your details are never shared or used for marketing.
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
